const countryCodesByLocale = require('../constants/country-codes-by-locale')
const mexicoTwoDigitAreaCodes = require('../constants/mexico-two-digit-area-code-regex')
const numbersOnly = require('./numbers-only')
const pipe = require('./pipe')
const checkRequired = require('./check-required')
const forwardError = require('./forward-error')
const isObject = require('./is-object')
const maskFromString = require('./mask-from-string')
const ERRORS = require('../constants/error-types')
const AsYouTypeFormatter = require('google-libphonenumber').AsYouTypeFormatter

const getPipedValidator = fn => {
  return pipe(
    checkRequired,
    forwardError(data => (fn(data.value) ? '' : ERRORS.invalid))
  )
}
const phonePlaceholder = countryCode => () => {
  return utils[countryCode]
    .getMask()
    .map(k => (k instanceof RegExp ? '0' : k))
    .join('')
}
// In much of europe the convention is to have a 0 in front for local calls
// but 0 is dropped when calling from abroad.
// This function allows for entering local + international formats
// from a single mask getter.
const prefixForEurope = fn => val =>
  fn(val).replace('0', /^0/.test(val) ? '#' : '')

const utils = {
  // USA
  1: {
    getMask: pipe(() => '(###) ###-####', maskFromString),
    validate: getPipedValidator(val => /^\d{10}$/.test(numbersOnly(val))),
    getPhonePlaceholder: phonePlaceholder(1),
  },
  // greece
  // 30: {},
  // netherlands
  // 31: {},
  // belgium
  // 32: {},
  // france
  33: {
    getMask: pipe(
      prefixForEurope(() => '0# ## ## ## ##'),
      maskFromString
    ),
    validate: getPipedValidator(val => {
      // local numbers always start with 0
      // but it's dropped internationally
      return /^0?[1-9]\d{8}$/.test(numbersOnly(val))
    }),
    getPhonePlaceholder: phonePlaceholder(33),
  },
  // spain
  34: {
    getMask: pipe(() => '### ### ###', maskFromString),
    validate: getPipedValidator(val => {
      // always a 9 digit number, never starts with 0
      return /^[1-9]\d{8}$/.test(numbersOnly(val))
    }),
    getPhonePlaceholder: phonePlaceholder(34),
  },
  // hungary
  // 36: {},
  // portugal
  // 351: {},
  // luxembourg
  // 352: {},
  // ireland
  353: {
    getMask: pipe(
      prefixForEurope(val => {
        // 01 dublin region
        if (/^0?1/.test(val)) {
          return '0# ############'
        }
        // 3-4 digit area codes
        if (/^0?[45]0/.test(val)) {
          return '0### ##########'
        }
        // 2-3 digit area codes
        return '0## ###########'
      }),
      maskFromString
    ),
    validate: getPipedValidator(val => {
      // might start with 0 and be 6-14 digits long
      return /^0?\d{6,15}$/.test(numbersOnly(val))
    }),
    getPhonePlaceholder: phonePlaceholder(353),
  },
  // malta
  // 356: {},
  // cypress
  // 357: {},
  // finland
  // 358: {},
  // bulgaria
  // 359: {},
  // lithuania
  // 370: {},
  // latvia
  // 371: {},
  // estonia
  // 372: {},
  // croatia
  // 385: {},
  // slovenia
  // 386: {},
  // italy
  // 39: {},
  // romania
  // 40: {},
  // czech republic
  // 420: {},
  // slovakia
  // 421: {},
  // austria
  // 43: {},
  // united kingdom
  44: {
    getMask: pipe(
      prefixForEurope(val => {
        // 2 digit area codes
        if (/^0?2/.test(val)) return '(0##) #### ####'
        // mobile #s
        if (/^0?7/.test(val)) return '0#### ######'
        // 3 digit area codes
        if (/^0?(11|1\d1)/.test(val)) return '(0###) ### ####'
        // 5 digit area codes
        if (/^0?1(38|52|53|69|76|94)/.test(val)) return '(0### ##) #####'
        // 4 digit area codes
        return '(0####) ######'
      }),
      maskFromString
    ),
    validate: getPipedValidator(val => {
      // might start with 0 and be 9-11 digits long
      return /^0?\d{9,10}$/.test(numbersOnly(val))
    }),
    getPhonePlaceholder: phonePlaceholder(44),
  },
  // denmark
  // 45: {},
  // sweden
  // 46: {},
  // poland
  // 48: {},
  // germany
  // 49: {},
  // mexico
  52: {
    getMask: pipe(val => {
      if (new RegExp('^1(' + mexicoTwoDigitAreaCodes + ')').test(val)) {
        return '# (##) ####-####'
      }

      if (new RegExp('^(' + mexicoTwoDigitAreaCodes + ')').test(val)) {
        return '(##) ####-####'
      }

      if (/^1/.test(val)) {
        return '# (###) ###-####'
      }

      return '(###) ###-####'
    }, maskFromString),
    validate: getPipedValidator(val => {
      // area codes never start with 0 or have 0 as the second number
      return /^[1-9][1-9]\d{8}$/.test(numbersOnly(val))
    }),
    getPhonePlaceholder: phonePlaceholder(52),
  },
  // china
  86: {
    getMask: pipe(val => {
      // mobile format
      if (/^1[^0-2]/.test(val)) {
        return '### #### ####'
      }

      if (/^[1-2]/.test(val)) {
        // 2 digit area + 8 digit numb (areas 1 & 2)
        return '## #### ####'
      }

      // 3 digit area + 7-8 digit num (areas 3-9)
      return '### ### #####'
    }, maskFromString),
    validate: getPipedValidator(val => val.length <= 15),
    getPhonePlaceholder: phonePlaceholder(86),
  },
  // india
  91: {
    getMask: pipe(() => '###########', maskFromString),
    validate: getPipedValidator(val => /^\d{10,11}$/.test(numbersOnly(val))),
    getPhonePlaceholder: phonePlaceholder(91),
  },

  /**
   * This function takes a string that represents user input of a phone number and then manually inputs that string,
   * one character at a time into the formatter. The result is a well formatted string representing a phone number.
   *
   * For example given the examples in the parameters below the returned string would be:
   *
   * (208) 123-4567
   *
   * @param str - i.e. 2081234567
   * @param locale - i.e. 'US' defaults to cc 1 'US'
   */
  format: (str, locale) => {
    let formatter = new AsYouTypeFormatter(locale || 'US')

    for (let i = 0; i < str.length; i++) {
      formatter.inputDigit(str[i])
    }

    return formatter.currentOutput_
  },
}

// named exports (mostly for testing)
exports.utils = utils

// util export
exports.getByLocale = config => {
  // manual country code passing, eg '52' (useful for i18n inputs)
  if (typeof config === 'string' || typeof config === 'number') {
    return utils[config] || {}
  }

  if (!isObject(config)) {
    return {}
  }

  return utils[countryCodesByLocale[config.locale]] || {}
}
